import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ColumnConfig, TableRow as TableRowType, ColumnKey } from '../types';
import { PredictionModelsDropdown } from './PredictionModelsDropdown';
import ResizeHandle from './ResizeHandle';
import PredictionsTableRow from './PredictionsTableRow';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  MOBILE_ACTIONS_COLUMN_WIDTH,
  MOBILE_MAX_COLUMN_WIDTH,
} from '../constants/tableLayout';

interface PredictionsTableProps {
  actionButtons: React.ReactNode[];
  columns: ColumnConfig[];
  tableData: TableRowType[];
  onCellChange: (
    rowIndex: number,
    column: ColumnKey,
    value: string | any[],
  ) => void;
  onAddRow: () => void;
  onDeleteRow?: (rowIndex: number) => void;
  onDuplicateRow?: (rowIndex: number) => void;
  onPaste?: (
    csvData: string,
    startRowIndex?: number,
    focusedColumnKey?: string,
  ) => void;
  onInsertRow?: (index: number) => void;
  disableAddButton?: boolean;
  disableModelSelection?: boolean;
  isRowValid?: (rowIndex: number) => boolean;
  hasValidationErrors?: boolean;
  highlightedRowIndex?: number | null;
  highlightedRowVariant?: 'amber' | 'green';
}

const PredictionsTable: React.FC<PredictionsTableProps> = ({
  actionButtons,
  columns,
  tableData,
  onCellChange,
  onAddRow,
  onDeleteRow,
  onDuplicateRow,
  onPaste,
  onInsertRow,
  disableAddButton = false,
  disableModelSelection = false,
  isRowValid,
  hasValidationErrors = false,
  highlightedRowIndex,
  highlightedRowVariant = 'amber',
}) => {
  const [showInvalidOnly, setShowInvalidOnly] = useState(false);
  // Below 768px the table swaps its hover-only controls (insert button, row
  // action panel, column resize handles) for a sticky row-number / actions
  // column, since none of the hover affordances can be reached on touch.
  const isMobile = useIsMobile();
  const [frozenInvalidIndices, setFrozenInvalidIndices] = useState<Set<number>>(new Set());
  useEffect(() => {
    if (!hasValidationErrors) {
      setShowInvalidOnly(false);
      setFrozenInvalidIndices(new Set());
    }
  }, [hasValidationErrors]);

  const handleTabChange = (val: string) => {
    if (val === 'invalid') {
      if (isRowValid) {
        const invalidSet = new Set(
          tableData.map((_, i) => i).filter((i) => !isRowValid(i)),
        );
        setFrozenInvalidIndices(invalidSet);
      }
      setShowInvalidOnly(true);
    } else {
      setFrozenInvalidIndices(new Set());
      setShowInvalidOnly(false);
    }
  };
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>(
    () => {
      const initialWidths: { [key: string]: number } = {};
      columns.forEach((column) => {
        initialWidths[column.key] = column.width || 150;
      });
      return initialWidths;
    },
  );

  const [focusedCell, setFocusedCell] = useState<{
    rowIndex: number;
    columnKey: string;
  } | null>(null);
  const [collapsedRows, setCollapsedRows] = useState<Set<number>>(new Set());

  const toggleRowExpand = (rowIndex: number) => {
    setCollapsedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowIndex)) {
        newSet.delete(rowIndex);
      } else {
        newSet.add(rowIndex);
      }
      return newSet;
    });
  };

  const handleInsertRow = useCallback(
    (index: number) => {
      setCollapsedRows((prev) => {
        const next = new Set<number>();
        prev.forEach((i) => next.add(i >= index ? i + 1 : i));
        next.add(index);
        return next;
      });
      onInsertRow?.(index);
    },
    [onInsertRow],
  );

  useEffect(() => {
    const newWidths: { [key: string]: number } = {};
    columns.forEach((column) => {
      newWidths[column.key] = columnWidths[column.key] || column.width || 150;
    });
    setColumnWidths(newWidths);
  }, [columns]);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      if (!onPaste) return;
      // Popover editors (person and genre search) render through a portal, so
      // their pastes bubble here through the React tree while their DOM lives
      // outside the table. Let the text land in the search box.
      if (!e.currentTarget.contains(e.target as Node)) return;

      e.preventDefault();
      const clipboardData = e.clipboardData.getData('text');

      if (clipboardData) {
        onPaste(
          clipboardData,
          focusedCell?.rowIndex || 0,
          focusedCell?.columnKey,
        );
      }
    },
    [onPaste, focusedCell],
  );

  const handleColumnResize = useCallback(
    (columnKey: string, delta: number) => {
      setColumnWidths((prev) => {
        const column = columns.find((col) => col.key === columnKey);
        const currentWidth = prev[columnKey] || column?.width || 150;
        const newWidth = Math.max(
          column?.minWidth || 100,
          Math.min(column?.maxWidth || 500, currentWidth + delta),
        );
        return { ...prev, [columnKey]: newWidth };
      });
    },
    [columns],
  );

  // The hover insert column (desktop) and the row-number / actions column
  // (phones) sit before the data columns, so the nth-child rules below have to
  // skip them. At most one of the two is rendered at a time.
  const showDesktopInsertColumn = Boolean(onInsertRow) && !isMobile;
  const leadingColumnCount = isMobile || showDesktopInsertColumn ? 1 : 0;

  const getRenderedWidth = (columnKey: string, width: number): number => {
    if (!isMobile) return width;
    const column = columns.find((col) => col.key === columnKey);
    if (column?.mobileWidth) return column.mobileWidth;
    return Math.min(width, MOBILE_MAX_COLUMN_WIDTH);
  };

  const generateColumnCSS = () => {
    const offset = leadingColumnCount + 1;
    return Object.entries(columnWidths)
      .map(([columnKey, width]) => {
        const columnIndex = columns.findIndex((col) => col.key === columnKey);
        const renderedWidth = getRenderedWidth(columnKey, width);
        return `
          .predictions-table th:nth-child(${columnIndex + offset}),
          .predictions-table td:nth-child(${columnIndex + offset}) {
            width: ${renderedWidth}px !important;
            max-width: ${renderedWidth}px !important;
            min-width: ${renderedWidth}px !important;
          }
        `;
      })
      .join('');
  };

  return (
    <div className="tw-flex tw-flex-col tw-flex-1 tw-overflow-hidden">
      <style data-testid="predictions-table-column-css">
        {generateColumnCSS()}
      </style>

      <div className="tw-flex-shrink-0 tw-mb-4">
        {/* Mobile-first: stacked on phones, the original side-by-side row from md up. */}
        <div className="tw-flex tw-flex-col tw-gap-3 md:tw-flex-row md:tw-justify-between md:tw-items-center md:tw-gap-0">
          <div>
            <h2 className="tw-mb-2 tw-text-xl tw-font-bold md:tw-mb-4 md:tw-text-2xl">
              Predictive Analytics Engine
            </h2>
            <PredictionModelsDropdown disabled={disableModelSelection} />
          </div>

          <div
            data-testid="predictions-table-actions"
            className="tw-flex tw-flex-row tw-flex-wrap tw-gap-2 md:tw-flex-nowrap"
          >
            {actionButtons.map((button, index) => (
              // Two buttons per line on phones; an odd last button (Process)
              // grows to the full width. From md up the buttons keep their
              // natural size in a single row, as before.
              <div
                key={index}
                data-testid="predictions-table-action"
                className="tw-grow tw-basis-[calc(50%-0.25rem)] [&_button]:tw-w-full md:tw-grow-0 md:tw-basis-auto md:[&_button]:tw-w-auto"
              >
                {button}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="tw-flex-shrink-0 tw-mb-3">
        <div className="tw-flex tw-justify-end tw-items-center tw-gap-2">
          <Tabs
            value={showInvalidOnly ? 'invalid' : 'all'}
            onValueChange={handleTabChange}
          >
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger
                value="invalid"
                disabled={!hasValidationErrors}
                className="tw-gap-1.5"
              >
                Invalid
                {hasValidationErrors && isRowValid && (
                  <Badge
                    variant="destructive"
                    className="tw-h-4 tw-min-w-4 tw-px-1 tw-text-[10px] tw-rounded-sm tw-pointer-events-none"
                  >
                    {tableData.filter((_, i) => !isRowValid(i)).length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Button
            onClick={onAddRow}
            className="tw-bg-green-600 hover:tw-bg-green-700"
            disabled={disableAddButton}
          >
            + Add Row
          </Button>
        </div>
      </div>

      {/* Fixed-height scroll area from md up; on phones the page scrolls instead. */}
      <div
        data-testid="predictions-table-body"
        className="md:tw-h-[calc(100vh-260px)]"
      >
        <Card className="tw-h-full tw-flex tw-flex-col tw-pt-0 tw-overflow-hidden">
          <div
            className="tw-flex-grow tw-overflow-x-auto tw-overflow-y-auto tw-h-full"
            style={{
              scrollbarWidth: 'thin',
              msOverflowStyle: 'scrollbar',
            }}
          >
            <Table
              className="predictions-table"
              style={{
                tableLayout: 'fixed',
                width: 'auto',
                minWidth: '100%',
              }}
              onPaste={handlePaste}
              tabIndex={0}
            >
              <TableHeader className="tw-sticky tw-top-0 tw-z-20 tw-shadow-sm">
                <TableRow>
                  {isMobile && (
                    <TableHead
                      scope="col"
                      aria-label="Row actions"
                      className="tw-sticky tw-left-0 tw-z-[1] tw-bg-muted !tw-px-1 tw-text-center tw-text-xs"
                      style={{
                        width: MOBILE_ACTIONS_COLUMN_WIDTH,
                        minWidth: MOBILE_ACTIONS_COLUMN_WIDTH,
                        maxWidth: MOBILE_ACTIONS_COLUMN_WIDTH,
                      }}
                    >
                      #
                    </TableHead>
                  )}
                  {showDesktopInsertColumn && (
                    <TableHead className="tw-w-0 tw-p-0" style={{ border: 'none' }} />
                  )}
                  {columns.map((column: ColumnConfig, index: number) => (
                    <TableHead
                      key={index}
                      className="tw-py-1"
                      style={{
                        ...column.style,
                      }}
                    >
                      {column.headerComponent
                        ? column.headerComponent()
                        : column.header}
                      {/* Resizing is mouse-driven, so the handles are desktop-only. */}
                      {column.resizable !== false && !isMobile && (
                        <ResizeHandle
                          onResize={(delta) =>
                            handleColumnResize(column.key, delta)
                          }
                        />
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {tableData.map((row: TableRowType, rowIndex: number) => {
                  const rowIsValid = isRowValid ? isRowValid(rowIndex) : true;
                  if (showInvalidOnly && !frozenInvalidIndices.has(rowIndex)) return null;
                  const isResolved = showInvalidOnly && rowIsValid && frozenInvalidIndices.has(rowIndex);
                  return (
                    <PredictionsTableRow
                      key={rowIndex}
                      row={row}
                      rowIndex={rowIndex}
                      columns={columns}
                      collapsedRows={collapsedRows}
                      onCellChange={onCellChange}
                      onDeleteRow={onDeleteRow}
                      onDuplicateRow={onDuplicateRow}
                      ontoggleRowExpand={toggleRowExpand}
                      onCellFocus={(rowIndex, columnKey) =>
                        setFocusedCell({ rowIndex, columnKey })
                      }
                      onInsertRow={onInsertRow && !showInvalidOnly ? handleInsertRow : undefined}
                      showInsertColumn={showDesktopInsertColumn}
                      isMobile={isMobile}
                      isValid={rowIsValid}
                      isResolved={isResolved}
                      highlightVariant={rowIndex === highlightedRowIndex ? highlightedRowVariant : null}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <CardFooter className="tw-flex-shrink-0 tw-border-t tw-py-2">
            <div className="tw-text-center tw-text-muted-foreground tw-w-full">
              <small>Total Rows: {tableData.length}</small>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default PredictionsTable;
